import Vue from "vue";
import App from "@/App.vue";
import router from "@/router";
import store from "@/store";
import axios from "@/utils/axios_base";
import helpers from "@/utils/helpers";
import title_mixin from "@/utils/title_mixin";
import { getBrand, applyBrandToDocument, applyBrandStartingLanguage } from "@/utils/brand";
import { startBrandLoad } from "@/utils/brandLoader";
// library
import NProgress from "vue-nprogress";
import Toasted from "vue-toasted";
import VModal from "vue-js-modal";
import InputMask from "vue-input-mask";
import VueGoodTablePlugin from "vue-good-table";
import "vue-good-table/dist/vue-good-table.css";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import "@/utils/input_validation";
import * as VueGoogleMaps from "vue2-google-maps";
import GmapCluster from "vue2-google-maps/dist/components/cluster";
import _ from "lodash"; // eslint-disable-line
import moment from "moment-timezone";
import "moment/locale/sv";
import "moment/locale/nb"; // Norwegian Bokmål
import "moment/locale/pl";
import "moment/locale/nl";
import FloatingVue from 'floating-vue'

import VueGtag from "vue-gtag";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import VueKonva from "vue-konva";
import i18n from "./i18n";
import $store from "@/store";

// components
import base_input from "@/components/base/_base_input";
import base_phone_input from "@/components/base/_base_phone_input.vue";
import base_textarea from "@/components/base/_base_textarea";
import base_select from "@/components/base/_base_select";
import base_switch from "@/components/base/_base_switch";
import base_icon from "@/components/base/_base_icon";
import loading_spinner from "@/components/base/loading_spinner";

// browser fingerprint
let fpPromise = FingerprintJS.load();
Vue.prototype.FingerprintJS = FingerprintJS;
Vue.prototype.fpPromise = fpPromise;
Vue.use(FloatingVue);

// SEC-334 white-label: the brand (Securdia default, CSG on the CSG address) is
// decided from the web address at start-up. The copy cached in the browser paints the
// first frame; whether a stored login belongs to this brand is decided only once the API
// has answered (utils/brandLoader.js), because a cache can be stale - a brand renamed or
// re-keyed since the last visit - and a stale cache must never log anyone out.
const brand = getBrand();
applyBrandToDocument(brand);
applyBrandStartingLanguage(brand); // SEC-334: the brand's own starting language, before the first paint
Vue.prototype.$brand = brand;
const brandReady = startBrandLoad(); // the brand comes from the reseller's branding (database); updates $brand in place

// nprogress
Vue.use(NProgress, {
  latencyThreshold: 50,
  router: false,
  http: false,
});

const nprogress = new NProgress();

axios.interceptors.request.use(
  async function(config) {
    nprogress.start();
    config.headers.B = await getFingerprint();
    // SEC-348: lets the API answer in the portal's current language (e.g. Dutch
    // emails) without storing a language preference anywhere - just what the
    // switcher is showing right now, same as every other request.
    config.headers['X-App-Locale'] = $store.getters.getAppLocale;
    return config;
  },
  function(error) {
    nprogress.done();
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function(response) {
    nprogress.done();
    return response;
  },
  function(error) {
    nprogress.done();
    return Promise.reject(error);
  }
);

// global axios
Vue.prototype.axios = axios;

// global moment
// Map app locale to moment locale (Norwegian uses 'nb' in moment)
const getMomentLocale = (locale) => {
  if (locale === 'no') return 'nb';
  return locale;
};

moment.locale(getMomentLocale($store.getters.getAppLocale));

$store.watch(
  (state) => state.appLocale,
  (newLocale) => {
    moment.locale(getMomentLocale(newLocale));
  },
  { immediate: true }
);
Vue.prototype.moment = moment;

// vue google map
// Map locale to Google Maps language code
const getGoogleMapsLanguage = () => {
  // SEC-331: the Safe-4 operator page (/t4/<token>) is Norwegian whatever is in
  // the store, and the Maps script language is fixed when the script loads —
  // after that the map's own controls cannot be re-translated. Read it off the
  // URL here so the map chrome matches the rest of the page.
  if (/^\/t4\//.test(window.location.pathname)) {
    const requested = new URLSearchParams(window.location.search).get("lang");
    return ["no", "sv", "en"].indexOf(requested) > -1 ? requested : "no";
  }

  const locale = $store.getters.getAppLocale;
  const languageMap = {
    'sv': 'sv',
    'no': 'no',
    'en': 'en',
    'pl': 'pl',
    'nl': 'nl'
  };
  return languageMap[locale] || 'en';
};

Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_API_KEY,
    libraries: "geometry,places,drawing",
    language: getGoogleMapsLanguage(),
    installComponents: true,
    loading: "async",
  },
});
Vue.component("GmapCluster", GmapCluster);

// plugins
Vue.use(VueGoodTablePlugin);
Vue.use(Toasted);
Vue.use(VModal, { dialog: true });
Vue.use(VueKonva);

// external components
Vue.component("ValidationProvider", ValidationProvider);
Vue.component("ValidationObserver", ValidationObserver);
Vue.component("input-mask", InputMask);

// internal components
Vue.component("BaseInput", base_input);
Vue.component("BasePhoneInput", base_phone_input);
Vue.component("BaseTextarea", base_textarea);
Vue.component("BaseSelect", base_select);
Vue.component("BaseSwitch", base_switch);
Vue.component("BaseIcon", base_icon);
Vue.component("LoadingSpinner", loading_spinner);

// mixins
Vue.mixin({
  methods: helpers,
});
Vue.mixin(title_mixin);

Vue.config.productionTip = false;

const app = new Vue({
  nprogress,
  router,
  store,
  i18n,
  render: (h) => h(App),
});

// Show the app once the brand of this web address is known, so a partner's visitor
// never sees the Securdia logo and colours first (SEC-334).
brandReady.then(() => {
  // SEC-334: analytics follows the brand. Securdia keeps its own property; a partner uses
  // the measurement id entered in its reseller's branding. A brand with no id is not measured at
  // all: the plugin is never installed, so no gtag script is loaded and nothing is sent to
  // a property belonging to someone else. Registered here, once the web address has told
  // us which brand this is, and before the app is mounted.
  if (brand.analyticsId) Vue.use(VueGtag, { config: { id: brand.analyticsId } });

  app.$mount("#app");
});

async function getFingerprint() {
  let visitorId = "";

  try {
    const fp = await fpPromise;
    const result = await fp.get();
    // eslint-disable-next-line
    const { screenFrame, ...components } = result.components;
    visitorId = FingerprintJS.hashComponents(components);
  } catch (error) {
    console.log(error);
  }

  return visitorId;
}
